// src/components/outputs/pieces/BlocksView.jsx — the block model → React elements.
//
// The one place the history card turns src/publish/blocks.js shapes into DOM.
// It never touches HTML as a string: no dangerouslySetInnerHTML, no sanitiser —
// every run becomes an element and every string becomes a text node, so a text
// run holding "<b>x</b>" prints those characters.
//
//   runs    text → text node · strong → <strong> · em → <em> · br → <br>
//           cite → <sup>[N]</sup>
//           link → <a target="_blank" rel="noopener noreferrer"> when the href is
//                  http(s); any other href (javascript:, data:, a bare path) renders
//                  its runs as plain text
//   blocks  heading → <h2>/<h3>/<h4> by level (1–3), scaled to the card
//           paragraph → <p> (pre-wrap, so the literal spacing of the copy survives)
//           list → <ul>/<ol> of <li>, with an inline list-style-type (disc / decimal):
//                  the Tailwind preflight removes every marker, inline wins
//           qa → the question in bold, then the answer blocks
//           slide → a bordered card "Slide N · tipo" + headline + body + visual cue
//                   in italic (title and cue label through i18n, as the serialisers)
//   Unknown block or run types are skipped; malformed input (not an array —
//   a string included —, a null, a run without text) renders nothing and never
//   throws.
//
// Props: { blocks, t, w, color, compact }
//   t       the renderer theme object (t.text, t.textMuted, t.border…) — the
//           console's light theme passes its own, so no colour is hard-wired
//   w       the rgba helper of the surface (w(alpha))
//   color   the accent for links and the slide card label (COLORS.accent when absent)
//   compact smaller type, for the secondary piece rows
//
// Tokens only (constants/theme.js). Nothing here reads the entry or the piece:
// PieceRow / ReadyToPublish choose what to show, this file only draws blocks.

import React from 'react';
import { useTranslation } from 'react-i18next';
import { COLORS, FONT_MONO, RADII } from '../../../constants/theme.js';
import { asRuns, asBlocks } from '../../../publish/blocks.js';
import { isHttpHref } from '../../../publish/normalize.js';
import { slideTitle } from '../../../publish/serialize/plain.js';

const defaultW = (o) => `rgba(255,255,255,${o})`;

/** Only an absolute http(s) address becomes a clickable link — the one rule of publish/normalize.js. */
export { isHttpHref };

// Type scale: [regular, compact] in px.
const SIZE = {
  body: [14, 13],
  h2: [18, 16],
  h3: [16, 15],
  h4: [15, 14],
  cue: [13, 12],
};
const px = (key, compact) => `${SIZE[key][compact ? 1 : 0]}px`;

/** A run list → inline React nodes (keys by position; a run list has no ids). */
export function renderRuns(runs, ctx) {
  const list = asRuns(runs);
  const out = [];
  list.forEach((r, i) => {
    switch (r.type) {
      case 'text':
        if (typeof r.text === 'string' && r.text !== '') out.push(<React.Fragment key={i}>{r.text}</React.Fragment>);
        break;
      case 'br':
        out.push(<br key={i} />);
        break;
      case 'cite': {
        const n = Number(r.n);
        if (Number.isFinite(n)) {
          out.push(
            <sup key={i} style={{ fontSize: '0.75em', lineHeight: 0, color: ctx.muted }}>[{n}]</sup>,
          );
        }
        break;
      }
      case 'strong':
        out.push(<strong key={i}>{renderRuns(r.runs, ctx)}</strong>);
        break;
      case 'em':
        out.push(<em key={i}>{renderRuns(r.runs, ctx)}</em>);
        break;
      case 'link': {
        const inner = renderRuns(r.runs, ctx);
        if (isHttpHref(r.href)) {
          const href = r.href.trim();
          out.push(
            <a
              key={i}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: ctx.accent, textDecoration: 'underline', overflowWrap: 'anywhere' }}
            >
              {inner.length ? inner : href}
            </a>,
          );
        } else if (inner.length) {
          out.push(<React.Fragment key={i}>{inner}</React.Fragment>);
        }
        break;
      }
      default:
        break;
    }
  });
  return out;
}

function Heading({ block, ctx }) {
  const level = Math.min(3, Math.max(1, Number(block.level) || 2));
  const Tag = `h${level + 1}`;
  const key = `h${level + 1}`;
  return (
    <Tag
      style={{
        margin: 0,
        fontSize: px(key, ctx.compact),
        fontWeight: 700,
        lineHeight: 1.35,
        color: ctx.text,
      }}
    >
      {renderRuns(block.runs, ctx)}
    </Tag>
  );
}

function Paragraph({ block, ctx }) {
  return (
    <p style={{ margin: 0, fontSize: px('body', ctx.compact), lineHeight: 1.65, whiteSpace: 'pre-wrap', color: ctx.text }}>
      {renderRuns(block.runs, ctx)}
    </p>
  );
}

function List({ block, ctx }) {
  const Tag = block.ordered ? 'ol' : 'ul';
  const items = Array.isArray(block.items) ? block.items : [];
  return (
    <Tag
      style={{
        margin: 0,
        paddingLeft: '22px',
        // Inline on purpose: Tailwind's preflight (src/index.css) sets list-style: none
        // on every ul/ol, and an inline value is the one thing it cannot override.
        listStyleType: block.ordered ? 'decimal' : 'disc',
        fontSize: px('body', ctx.compact),
        lineHeight: 1.65,
        color: ctx.text,
      }}
    >
      {items.map((it, i) => (
        <li key={i} style={{ whiteSpace: 'pre-wrap' }}>{renderRuns(it, ctx)}</li>
      ))}
    </Tag>
  );
}

function Qa({ block, ctx }) {
  const question = renderRuns(block.question, ctx);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: ctx.compact ? '4px' : '6px' }}>
      {question.length > 0 && (
        <p style={{ margin: 0, fontSize: px('body', ctx.compact), lineHeight: 1.5, whiteSpace: 'pre-wrap', color: ctx.text }}>
          <strong>{question}</strong>
        </p>
      )}
      <Blocks blocks={block.answer} ctx={ctx} />
    </div>
  );
}

function Slide({ block, ctx }) {
  const { tr } = ctx;
  const headline = renderRuns(block.headline, ctx);
  const cue = typeof block.visualCue === 'string' && block.visualCue.trim() ? block.visualCue.trim() : '';
  return (
    <section
      style={{
        border: `1px solid ${ctx.border}`,
        borderRadius: RADII.control,
        background: ctx.w(0.03),
        padding: ctx.compact ? '10px 12px' : '12px 14px',
        display: 'flex',
        flexDirection: 'column',
        gap: ctx.compact ? '6px' : '8px',
      }}
    >
      <div
        style={{
          fontSize: '10px',
          fontFamily: FONT_MONO,
          color: ctx.accent,
          letterSpacing: '1px',
          textTransform: 'uppercase',
          fontWeight: 600,
        }}
      >
        {slideTitle(block)}
      </div>
      {headline.length > 0 && (
        <p style={{ margin: 0, fontSize: px('h4', ctx.compact), fontWeight: 700, lineHeight: 1.4, whiteSpace: 'pre-wrap', color: ctx.text }}>
          {headline}
        </p>
      )}
      <Blocks blocks={block.body} ctx={ctx} />
      {cue && (
        <p style={{ margin: 0, fontSize: px('cue', ctx.compact), lineHeight: 1.5, fontStyle: 'italic', color: ctx.muted, whiteSpace: 'pre-wrap' }}>
          {tr('pieces.visualCue')}: {cue}
        </p>
      )}
    </section>
  );
}

/** The block list itself (recursive for qa answers and slide bodies). */
function Blocks({ blocks, ctx }) {
  const list = asBlocks(blocks);
  if (!list.length) return null;
  return (
    <>
      {list.map((b, i) => {
        switch (b.type) {
          case 'heading': return <Heading key={i} block={b} ctx={ctx} />;
          case 'paragraph': return <Paragraph key={i} block={b} ctx={ctx} />;
          case 'list': return <List key={i} block={b} ctx={ctx} />;
          case 'qa': return <Qa key={i} block={b} ctx={ctx} />;
          case 'slide': return <Slide key={i} block={b} ctx={ctx} />;
          default: return null;
        }
      })}
    </>
  );
}

export default function BlocksView({ blocks, t = {}, w = defaultW, color, compact = false }) {
  const { t: tr } = useTranslation('outputs');
  // `blocks` is a block LIST: a bare string is not one here (asBlocks would make
  // it a paragraph), so anything but an array renders nothing.
  const list = Array.isArray(blocks) ? asBlocks(blocks) : [];
  if (!list.length) return null;
  const theme = t && typeof t === 'object' ? t : {};
  const ctx = {
    compact: Boolean(compact),
    w: typeof w === 'function' ? w : defaultW,
    tr,
    text: theme.text || COLORS.ink,
    muted: theme.textMuted || COLORS.textSecondary,
    border: theme.border || COLORS.border,
    accent: color || COLORS.accent,
  };
  return (
    <div
      data-testid="blocks-view"
      style={{ display: 'flex', flexDirection: 'column', gap: compact ? '6px' : '10px', minWidth: 0 }}
    >
      <Blocks blocks={list} ctx={ctx} />
    </div>
  );
}
